import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import Color from "colorjs.io"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Calculate contrast ratio between two colors (WCAG standard)
 */
export function getContrastRatio(foreground: string, background: string): number {
  try {
    const fgColor = new Color(foreground);
    const bgColor = new Color(background);
    const fgLum = fgColor.luminance;
    const bgLum = bgColor.luminance;
    const lighter = Math.max(fgLum, bgLum);
    const darker = Math.min(fgLum, bgLum);
    return (lighter + 0.05) / (darker + 0.05);
  } catch {
    return 0;
  }
}

/**
 * Check if contrast ratio meets minimum WCAG AA standard (4.5:1)
 */
export function hasMinimumContrast(foreground: string, background: string): boolean {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= 4.5;
}

/**
 * Generate a contrasting text color based on background
 * Returns dark text (#1A1A1A) for light backgrounds, or a lighter color for dark backgrounds
 * Never returns white to ensure readability
 */
export function getContrastingTextColor(backgroundColor: string): string {
  try {
    const bgColor = new Color(backgroundColor);
    const lightness = bgColor.to("hsl").coords[2];
    // If background is light (lightness > 70%), use dark text
    // If background is dark, use light gray instead of white
    return lightness > 70 ? "#1A1A1A" : "#E5E5E5";
  } catch {
    return "#1A1A1A";
  }
}

/**
 * Ensure text color has sufficient contrast with background
 * If not, returns a contrasting alternative
 * Never allows pure white text
 */
export function ensureTextContrast(textColor: string, backgroundColor: string): string {
  // Never allow white text
  if (textColor.toUpperCase() === "#FFFFFF" || textColor.toUpperCase() === "#FFF") {
    return getContrastingTextColor(backgroundColor);
  }

  // Check if contrast is sufficient
  if (hasMinimumContrast(textColor, backgroundColor)) {
    return textColor;
  }

  // If not enough contrast, return a contrasting alternative
  return getContrastingTextColor(backgroundColor);
} 